import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Briefcase, Users, Search, MapPin, Clock, TrendingUp,
  ArrowRight, Building2, Wrench, Star, ChevronRight, Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MarketplaceHomePage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/marketplace/stats`)
      .then(res => res.json())
      .then(setStats)
      .catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <Link to="/" className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1">
            <Home className="w-4 h-4" /> {t('common.home')}
          </Link>
        </div>
      </div>

      {/* Hero Section - TWO PRIMARY ACTIONS */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
            {t('marketplace.hero.title')}
          </h1>
          <p className="text-xl text-blue-100 text-center mb-12 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>

          {/* Two Equal Primary Actions */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* HANKED - Find Work / Procurement */}
            <Card 
              className="bg-white/10 backdrop-blur border-white/20 hover:bg-white/20 transition-all cursor-pointer group"
              onClick={() => navigate('/marketplace/hanked')}
              data-testid="marketplace-hanked-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Briefcase className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-3">{t('marketplace.hero.hankedLabel')}</h2>
                <p className="text-blue-100 mb-6">{t('marketplace.hero.hankedDesc')}</p>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white w-full group-hover:shadow-lg">
                  {t('marketplace.hanked.cta')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-sm text-blue-200 mt-4">
                  {stats?.hanked_count || 0}+ {t('marketplace.listing.applications')}
                </p>
              </CardContent>
            </Card>

            {/* ALLTÖÖVÕTT - Offer Services / Subcontracting */}
            <Card 
              className="bg-white/10 backdrop-blur border-white/20 hover:bg-white/20 transition-all cursor-pointer group"
              onClick={() => navigate('/marketplace/alltooovott')}
              data-testid="marketplace-alltooovott-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Wrench className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-3">{t('marketplace.hero.alltooovottLabel')}</h2>
                <p className="text-blue-100 mb-6">{t('marketplace.hero.alltooovottDesc')}</p>
                <Button className="bg-green-500 hover:bg-green-600 text-white w-full group-hover:shadow-lg">
                  {t('marketplace.alltooovott.cta')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-sm text-blue-200 mt-4">
                  {stats?.alltooovott_count || 0}+ {t('nav.services')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Quick Explanation */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-8 h-8 text-orange-600" />
              <h3 className="text-xl font-bold text-orange-900">{t('marketplace.hanked.title')}</h3>
            </div>
            <p className="text-orange-800 mb-4">{t('marketplace.hanked.description')}</p>
            <ul className="space-y-2 text-sm text-orange-700">
              <li className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                {t('marketplace.subscription.proRequired')}
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                {t('marketplace.subscription.freeTrial')}
              </li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Wrench className="w-8 h-8 text-green-600" />
              <h3 className="text-xl font-bold text-green-900">{t('marketplace.alltooovott.title')}</h3>
            </div>
            <p className="text-green-800 mb-4">{t('marketplace.alltooovott.description')}</p>
            <ul className="space-y-2 text-sm text-green-700">
              <li className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                {t('marketplace.subscription.freeTrial')}
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                {t('marketplace.subscription.benefits.aiMatching')}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="bg-white py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8">{t('common.popularCategories')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stats.top_categories?.slice(0, 5).map((cat, i) => {
                const slug = String(cat.category || '').toLowerCase();
                const label = t(`categories.${slug}`, { defaultValue: '' }) || slug.replace(/_/g, ' ');
                return (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 text-center" data-testid={`marketplace-cat-${slug}`}>
                    <p className="font-medium text-gray-900">{label}</p>
                    <p className="text-sm text-gray-500">{t('marketplace.listingsCount', { count: cat.count })}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Countries */}
      {stats?.top_countries?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-center mb-8">{t('common.browseByCountry')}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {stats.top_countries.map((country, i) => {
              // Map English country name from backend → ISO code → localized name
              const COUNTRY_MAP = {
                Estonia: 'EE', Finland: 'FI', Sweden: 'SE', Norway: 'NO',
                Denmark: 'DK', Latvia: 'LV', Lithuania: 'LT', Germany: 'DE',
                Poland: 'PL', Netherlands: 'NL'
              };
              const code = COUNTRY_MAP[country.country] || country.country;
              const localized = t(`countries.${code}`, { defaultValue: country.country });
              return (
                <Badge 
                  key={i}
                  variant="outline"
                  className="px-4 py-2 text-base cursor-pointer hover:bg-blue-50"
                  onClick={() => navigate(`/marketplace/hanked?country=${country.country}`)}
                  data-testid={`marketplace-country-${code}`}
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  {localized} ({country.count})
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceHomePage;
